import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ExternalLink,
  Package,
  MapPin,
  Copy,
  Share2,
  Tag,
  Clock,
  Store,
  Lock,
  TrendingUp,
  Sun,
  Moon,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { getPublicDeal, getDealShareUrl } from "../services/DealsService";
import { getPublicAffiliateLink } from "../services/ProductsService";
import type { Deal } from "../services/DealsService";
import { toast } from "react-toastify";
import { getProductImageUrl } from "../utils/imageUtils";
import { getRetailerProductUrl } from "../utils/productUrls";
import { useTheme } from "../context/ThemeContext";
import { useStores } from "../hooks/useStores";
import type { StoreData, BestBuyStore, TargetStore } from "../hooks/useStores";
import { normalizeStoreNumber } from "../utils/userUtils";
import type {
  WalmartStore,
  HomeDepotStore,
  DollarGeneralStore,
} from "../utils/storeUtils";
import { getRetailerDisplayName, type RetailerType } from "../config/retailers";
import { getSafeAffiliateDestination } from "../components/guest-deals/GuestDealsUi.utils";
import { captureUtm, track } from "../utils/analytics";

const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

function useOGMeta(meta: Record<string, string>) {
  useEffect(() => {
    const created: HTMLMetaElement[] = [];
    const origTitle = document.title;
    if (meta["og:title"]) document.title = meta["og:title"] + " | DealHawk";
    Object.entries(meta).forEach(([key, value]) => {
      if (!value) return;
      const isOG = key.startsWith("og:");
      const sel = isOG ? `meta[property="${key}"]` : `meta[name="${key}"]`;
      const existing = document.querySelector(sel) as HTMLMetaElement | null;
      if (existing) {
        existing.setAttribute("content", value);
      } else {
        const el = document.createElement("meta");
        el.setAttribute(isOG ? "property" : "name", key);
        el.setAttribute("content", value);
        document.head.appendChild(el);
        created.push(el);
      }
    });
    return () => {
      document.title = origTitle;
      created.forEach((t) => t.remove());
    };
  }, [meta]);
}

function useStoreAddress(storeId: string, retailer: string) {
  const retailerType = (retailer || "walmart") as RetailerType;
  const { stores } = useStores<StoreData>(retailerType);

  return useMemo(() => {
    if (!stores || !storeId) return null;
    const label = retailerLabel(retailer);
    if (retailer === "home_depot") {
      const norm = normalizeStoreNumber(storeId, "home_depot").padStart(4, "0");
      const s = (stores as HomeDepotStore[]).find((st) => st.store_number === norm);
      return s ? { address: s.address_1, name: s.store_title } : null;
    }
    if (retailer === "dollar_general") {
      const s = (stores as DollarGeneralStore[]).find((st) => st.storeNumber === storeId);
      return s ? { address: s.address, name: `${label} #${storeId}` } : null;
    }
    if (retailer === "target") {
      const s = (stores as TargetStore[]).find((st) => String(st["Store No."]) === storeId);
      return s ? { address: s.Street, name: s.Name || `${label} #${storeId}` } : null;
    }
    if (retailer === "bestbuy") {
      const s = (stores as BestBuyStore[]).find((st) => st.store_number === storeId);
      return s ? { address: s.street, name: s.name || `${label} #${storeId}` } : null;
    }
    const s = (stores as WalmartStore[]).find((st) => st.store_id === storeId);
    return s ? { address: s.address, name: `${label} #${storeId}` } : null;
  }, [stores, storeId, retailer]);
}

const retailerLabel = (retailer?: string) =>
  getRetailerDisplayName((retailer || "walmart") as RetailerType);

export default function PublicProductDetailPage() {
  const { dealId } = useParams<{ dealId: string }>();
  const { isDark, toggleTheme } = useTheme();
  const [affiliateLoading, setAffiliateLoading] = useState(false);

  useEffect(() => {
    captureUtm();
  }, []);

  const { data: deal, isLoading, error } = useQuery<Deal>({
    queryKey: ["deal", dealId],
    queryFn: () => getPublicDeal(dealId!),
    enabled: !!dealId,
    retry: false,
  });

  const isOnline = deal?.storeId === "online";
  const storeInfo = useStoreAddress(isOnline ? "" : (deal?.storeId || ""), deal?.retailer || "walmart");
  const isHomeDepot = deal?.retailer === "home_depot";
  const shareUrl = dealId ? getDealShareUrl(dealId) : "";

  const ogImage = deal ? getProductImageUrl(deal.image) : "";
  const discountPercent = deal && deal.msrp > deal.storePrice
    ? Math.round(((deal.msrp - deal.storePrice) / deal.msrp) * 100) : 0;
  const ogDesc = deal
    ? `${fmt(deal.storePrice)}${discountPercent > 0 ? ` — ${discountPercent}% off` : ""} | Found on DealHawk`
    : "Check out this deal on DealHawk";

  useOGMeta({
    "og:type": "product",
    "og:title": deal?.name || "Deal | DealHawk",
    "og:description": ogDesc,
    "og:image": ogImage,
    "og:url": typeof window !== "undefined" ? window.location.href : "",
    "og:site_name": "DealHawk",
    "twitter:card": "summary_large_image",
    "twitter:title": deal?.name || "Deal | DealHawk",
    "twitter:description": ogDesc,
    "twitter:image": ogImage,
  });

  const copy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied!`);
    } catch { toast.error("Failed to copy"); }
  };

  const productUrl = getRetailerProductUrl({
    url: deal?.url,
    retailer: deal?.retailer,
    storeId: deal?.storeId,
  });

  const handleAffiliateClick = async () => {
    if (affiliateLoading || !deal?.productKey) return;

    // Open synchronously so mobile browsers do not block the destination while
    // the backend resolves the protected affiliate URL.
    const tab = window.open("about:blank", "_blank");
    if (tab) tab.opener = null;
    setAffiliateLoading(true);
    try {
      const resolvedUrl = await getPublicAffiliateLink(deal.productKey);
      const destination = getSafeAffiliateDestination(resolvedUrl, "");
      if (!destination) throw new Error("Affiliate response did not contain a safe destination");

      const source = new URLSearchParams(window.location.search).get("utm_source") || "shared_deal";
      track("guest_outbound_clicked", {
        surface: "web",
        retailer: deal.retailer,
        trigger: "shared_deal",
        source,
      });
      if (tab && !tab.closed) tab.location.replace(destination);
      else window.location.assign(destination);
    } catch {
      if (tab && !tab.closed) tab.close();
      toast.error("The retailer link is temporarily unavailable. Please try again.");
    } finally {
      setAffiliateLoading(false);
    }
  };

  if (!dealId) {
    return (
      <div className="flex items-center justify-center min-h-[80vh] px-4">
        <div className="text-center max-w-sm">
          <Package className="mx-auto h-14 w-14 text-gray-300 dark:text-dark-600 mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Invalid Deal Link</h1>
          <p className="text-sm text-gray-500 dark:text-dark-400 mb-5">This link appears to be incomplete or broken.</p>
          <Link to="/" className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors">
            Go to DealHawk <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[80vh]">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-200 border-t-primary-600" />
      </div>
    );
  }

  if (error || !deal) {
    return (
      <div className="flex items-center justify-center min-h-[80vh] px-4">
        <div className="text-center max-w-sm">
          <Package className="mx-auto h-14 w-14 text-gray-300 dark:text-dark-600 mb-4" />
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Deal Not Found</h1>
          <p className="text-sm text-gray-500 dark:text-dark-400 mb-5">This deal may have been removed or the link is incorrect.</p>
          <Link to="/get-started" className="inline-flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors">
            Sign Up for DealHawk <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] bg-gray-50 dark:bg-dark-950">
      <div className="max-w-2xl mx-auto px-4 py-6 sm:py-10">

        {/* Main Product Card */}
        <div className="bg-white dark:bg-dark-900 rounded-2xl shadow-sm border border-gray-100 dark:border-dark-800 overflow-hidden">

          {/* Image + Pricing Hero */}
          <div className="p-3 sm:p-8 pb-0 sm:pb-0">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-8 items-center sm:items-start">
              {/* Image */}
              <div className="relative flex-shrink-0">
                <div className="w-36 h-36 sm:w-48 sm:h-48 rounded-xl sm:rounded-2xl bg-gray-50 dark:bg-dark-800 border border-gray-100 dark:border-dark-700 flex items-center justify-center overflow-hidden">
                  {deal.image ? (
                    <img src={getProductImageUrl(deal.image)} alt={deal.name} className="w-full h-full object-contain p-1 sm:p-2" />
                  ) : (
                    <Package className="h-8 w-8 sm:h-16 sm:w-16 text-gray-300 dark:text-dark-600" />
                  )}
                </div>
                {discountPercent > 0 && (
                  <div className="absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 bg-red-500 text-white px-1.5 sm:pl-2 sm:pr-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold shadow-lg shadow-red-500/30">
                    -{discountPercent}%
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0 w-full text-center sm:text-left">
                <div className="flex items-center justify-center sm:justify-start gap-1.5 mb-1 sm:mb-2">
                  <span className="text-[10px] sm:text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30 px-1.5 sm:px-2 py-0.5 rounded-full">
                    {retailerLabel(deal.retailer)}
                  </span>
                  <span className="text-[10px] sm:text-xs text-gray-400 dark:text-dark-300 truncate">{deal.category}</span>
                </div>

                <h1 className="text-sm sm:text-xl font-bold text-gray-900 dark:text-white leading-snug mb-1.5 sm:mb-3 line-clamp-3 sm:line-clamp-none">
                  {deal.name}
                </h1>

                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-x-3 gap-y-1 text-[10px] sm:text-xs text-gray-500 dark:text-dark-200">
                  {deal.upc && (
                    <span className="inline-flex items-center gap-1">
                      <Tag className="h-2.5 w-2.5 sm:h-3 sm:w-3" /> {isHomeDepot ? "Item" : "SKU"}: <span className="font-mono text-gray-700 dark:text-dark-200">{deal.upc}</span>
                      <button onPointerDown={() => copy(deal.upc, "SKU")} className="p-0.5 hover:bg-gray-100 dark:hover:bg-dark-700 rounded">
                        <Copy className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                      </button>
                    </span>
                  )}
                  <span className={`inline-flex items-center gap-1 font-medium ${deal.inStock ? "text-emerald-600 dark:text-emerald-400" : "text-red-500 dark:text-red-400"}`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${deal.inStock ? "bg-emerald-500" : "bg-red-500"}`} />
                    {deal.inStock ? "In Stock" : "Out of Stock"}
                  </span>
                  {deal.aisle && (
                    <span className="inline-flex items-center gap-1">
                      <MapPin className="h-2.5 w-2.5 sm:h-3 sm:w-3" /> {deal.aisle}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div className="px-3 sm:px-8 pt-3 sm:pt-6">
            <div className="border-t border-gray-100 dark:border-dark-800" />
          </div>

          {/* Price Comparison */}
          <div className="px-3 sm:px-8 py-3 sm:py-5">
            <div className={`grid gap-2 sm:gap-3 text-center ${isOnline || isHomeDepot ? "grid-cols-2" : "grid-cols-3"}`}>
              <div>
                <p className="text-[9px] sm:text-[11px] uppercase tracking-wider text-gray-400 dark:text-dark-300 mb-0.5">{isOnline ? "Price" : "In-Store"}</p>
                <p className="text-sm sm:text-lg font-bold text-emerald-600 dark:text-emerald-400">{fmt(deal.storePrice)}</p>
              </div>
              {!isOnline && !isHomeDepot && (
                <div>
                  <p className="text-[9px] sm:text-[11px] uppercase tracking-wider text-gray-400 dark:text-dark-300 mb-0.5">Online</p>
                  <p className="text-sm sm:text-lg font-bold text-gray-700 dark:text-white">{fmt(deal.onlinePrice)}</p>
                </div>
              )}
              <div>
                <p className="text-[9px] sm:text-[11px] uppercase tracking-wider text-gray-400 dark:text-dark-300 mb-0.5">MSRP</p>
                <p className="text-sm sm:text-lg font-bold text-gray-400 dark:text-dark-300 line-through">{fmt(deal.msrp)}</p>
              </div>
            </div>
          </div>

          {/* Store Location / Online Badge */}
          {isOnline ? (
            <div className="px-3 sm:px-8 pb-1 sm:pb-2">
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg sm:rounded-xl p-2.5 sm:p-4 flex items-center gap-2.5 sm:gap-3">
                <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-md sm:rounded-lg bg-blue-100 dark:bg-blue-900/40 flex items-center justify-center flex-shrink-0">
                  <ExternalLink className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">Online Deal</p>
                  <p className="text-[10px] sm:text-xs text-gray-500 dark:text-dark-300 mt-0.5">Available on {retailerLabel(deal.retailer)}.com</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="px-3 sm:px-8 pb-1 sm:pb-2">
              <div className="bg-gray-50 dark:bg-dark-800/50 rounded-lg sm:rounded-xl p-2.5 sm:p-4 flex items-start gap-2.5 sm:gap-3">
                <div className="w-7 h-7 sm:w-9 sm:h-9 rounded-md sm:rounded-lg bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center flex-shrink-0">
                  <Store className="h-3.5 w-3.5 sm:h-4.5 sm:w-4.5 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white">
                    {storeInfo?.name || `${retailerLabel(deal.retailer)} Store #${deal.storeId}`}
                  </p>
                  {storeInfo?.address ? (
                    <p className="text-[10px] sm:text-xs text-gray-500 dark:text-dark-300 mt-0.5">{storeInfo.address}</p>
                  ) : (
                    <p className="text-[10px] sm:text-xs text-gray-400 dark:text-dark-300 mt-0.5">Store #{deal.storeId}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Primary CTA */}
          <div className="px-3 sm:px-8 pt-3 sm:pt-5">
            {isOnline ? (
              <>
                <p className="text-[10px] sm:text-xs text-gray-500 dark:text-dark-300 text-center mb-1.5">
                  Price and availability can change at the retailer.
                </p>
                <button
                  type="button"
                  onClick={() => void handleAffiliateClick()}
                  disabled={affiliateLoading || !deal.productKey}
                  className="flex items-center justify-center gap-2 w-full py-2.5 sm:py-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {affiliateLoading ? (
                    <Loader2 className="h-3.5 w-3.5 sm:h-4 sm:w-4 animate-spin" />
                  ) : (
                    <ExternalLink className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  )}
                  Shop at {retailerLabel(deal.retailer)}
                </button>
                <p className="mt-2 text-center text-[9px] sm:text-[11px] text-gray-400 dark:text-dark-300">
                  Affiliate link — DealHawk may earn a commission from your purchase at no extra cost to you.
                </p>
                <Link
                  to="/deals"
                  className="mt-2 flex items-center justify-center text-[10px] sm:text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
                >
                  Browse more deals
                </Link>
              </>
            ) : (
              <>
                <p className="text-[10px] sm:text-xs text-gray-500 dark:text-dark-300 text-center mb-1.5">
                  Sign up to check price & stock at stores near you
                </p>
                <Link
                  to="/get-started"
                  className="flex items-center justify-center gap-2 w-full py-2.5 sm:py-3.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg sm:rounded-xl font-semibold text-xs sm:text-sm transition-colors"
                >
                  <MapPin className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  Check stock
                </Link>
              </>
            )}
          </div>

          {/* Secondary links + timestamp */}
          <div className="px-3 sm:px-8 pt-2 pb-3 sm:pb-4 flex items-center justify-between">
            {deal.createdAt > 0 && (
              <span className="text-[9px] sm:text-[11px] text-gray-400 dark:text-dark-300 inline-flex items-center gap-1">
                <Clock className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                Shared {new Date(deal.createdAt).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
              </span>
            )}
            <div className="flex items-center gap-3 ml-auto">
              {!isOnline && productUrl && (
                <a
                  href={productUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-gray-400 dark:text-dark-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  <ExternalLink className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                  {retailerLabel(deal.retailer)}.com
                </a>
              )}
              {shareUrl && (
                <button
                  onPointerDown={() => copy(shareUrl, "Share link")}
                  className="inline-flex items-center gap-1 text-[10px] sm:text-xs text-gray-400 dark:text-dark-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  <Share2 className="h-2.5 w-2.5 sm:h-3 sm:w-3" />
                  Share
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Locked Price History Card */}
        <div className="mt-3 sm:mt-4 bg-white dark:bg-dark-900 rounded-2xl border border-gray-100 dark:border-dark-800 p-3 sm:p-5 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/80 to-white/40 dark:from-dark-900 dark:via-dark-900/80 dark:to-dark-900/40 z-10 flex flex-col items-center justify-center">
            <Lock className="h-5 w-5 sm:h-6 sm:w-6 text-gray-300 dark:text-dark-400 mb-1" />
            <p className="text-[10px] sm:text-xs font-medium text-gray-500 dark:text-dark-200">Price history available for members</p>
          </div>
          <div className="select-none pointer-events-none" aria-hidden>
            <div className="flex items-center justify-between mb-2 sm:mb-3">
              <span className="text-[10px] sm:text-xs font-semibold text-gray-400 dark:text-dark-400">Price History</span>
              <TrendingUp className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-gray-300 dark:text-dark-400" />
            </div>
            <div className="h-16 sm:h-24 flex items-end gap-[2px] sm:gap-[3px]">
              {[38,52,44,62,48,70,55,64,42,56,46,50,58,40,65,52,48].map((h, i) => (
                <div key={i} className="flex-1 rounded-sm bg-primary-200/60 dark:bg-primary-800/40" style={{ height: `${h}%` }} />
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-6 sm:mt-8 pb-6 flex items-center justify-between">
          <span className="text-[10px] sm:text-xs text-gray-400 dark:text-dark-300">DealHawk</span>
          <button
            onPointerDown={toggleTheme}
            className="flex items-center gap-1.5 px-2.5 py-1.5 text-[10px] sm:text-xs text-gray-500 dark:text-dark-200 bg-white dark:bg-dark-800 border border-gray-200 dark:border-dark-600 rounded-lg hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors"
          >
            {isDark ? <Sun className="h-3 w-3" /> : <Moon className="h-3 w-3" />}
            {isDark ? "Light" : "Dark"}
          </button>
        </div>
      </div>
    </div>
  );
}
